import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useSettings } from './useSettings';

interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  message: string;
  property_id?: string;
  type?: 'property' | 'acquisition' | 'generic';
}

export const useContactForm = () => {
  const [loading, setLoading] = useState(false);
  const { settings } = useSettings();
  const notificationEmail = 'info@molinaroimmobiliare.it';

  const trackConversion = (type: string) => {
    try {
      console.log(`Tracking conversion for: ${type}`);
      
      // Google Ads Conversion
      if (settings?.google_ads_conversion_id && window.gtag) {
        window.gtag('event', 'conversion', {
          'send_to': `${settings.google_ads_id}/${settings.google_ads_conversion_id}`,
          'event_category': 'Contact',
          'event_label': type,
          'value': 1,
          'transaction_id': Date.now().toString()
        });
        console.log('Google Ads conversion tracked');
      }

      // Facebook Pixel
      if (settings?.facebook_pixel_id && window.fbq) {
        window.fbq('track', 'Lead', {
          content_name: type,
          content_category: 'Contact',
          value: 1,
          currency: 'EUR'
        });
        console.log('Facebook Pixel conversion tracked');
      }

      // Google Tag Manager
      if (settings?.gtm_id && window.dataLayer) {
        window.dataLayer.push({
          'event': 'form_submission',
          'form_type': type,
          'form_success': true
        });
        console.log('GTM event pushed');
      }
    } catch (error) {
      console.error('Error tracking conversion:', error);
    }
  };

  const handlePhoneClick = (phone: string, isMobile: boolean) => {
    // Track phone click conversion
    trackConversion('phone_click');

    // On mobile, directly start the call
    if (isMobile) {
      window.location.href = `tel:${phone}`;
    }
  };

  const handleWhatsAppClick = (phone: string) => {
    // Track WhatsApp click conversion
    trackConversion('whatsapp_click');

    // Open WhatsApp
    window.open(`https://wa.me/${phone.replace(/\D/g, '')}`, '_blank');
  };

  // Implementazione diretta dell'invio email tramite Brevo API
  const sendEmailNotification = async (contactData: ContactFormData, propertyDetails?: {
    title?: string;
    ref?: string;
    url?: string;
  }) => {
    try {
      // Utilizziamo direttamente l'API di Brevo invece della Edge Function
      const BREVO_API_KEY = "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";
      
      // Prepara il contenuto HTML dell'email con design migliorato
      let htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 0; }
            .container { max-width: 600px; margin: 0 auto; padding: 20px; }
            .header { background-color: #1D2B4E; color: white; padding: 20px; border-radius: 5px 5px 0 0; }
            .content { background-color: #f9f9f9; padding: 20px; border-radius: 0 0 5px 5px; border: 1px solid #e0e0e0; border-top: none; }
            .property-box { background-color: #fff; border-left: 4px solid #A3924D; padding: 15px; margin: 20px 0; }
            .message-box { background-color: #fff; padding: 15px; margin: 20px 0; border-radius: 5px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
            .contact-info { margin-bottom: 20px; }
            .contact-label { font-weight: bold; color: #1D2B4E; }
            .contact-value { margin-bottom: 10px; }
            .footer { text-align: center; margin-top: 20px; font-size: 12px; color: #777; padding-top: 20px; border-top: 1px solid #eee; }
            h1 { color: white; margin: 0; font-size: 24px; }
            h2 { color: #1D2B4E; border-bottom: 2px solid #A3924D; padding-bottom: 10px; font-size: 18px; margin-top: 30px; }
            h3 { color: #1D2B4E; font-size: 16px; margin-bottom: 10px; }
            a { color: #1D2B4E; text-decoration: none; }
            a:hover { text-decoration: underline; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>Nuova Richiesta di Contatto</h1>
            </div>
            <div class="content">
              <div class="contact-info">
                <p><strong>Tipo di richiesta:</strong> ${contactData.type === 'property' ? 'Informazioni su immobile' : 
                                                        contactData.type === 'acquisition' ? 'Valutazione immobile' : 
                                                        'Richiesta generica'}</p>
                <p><strong>Data:</strong> ${new Date().toLocaleString('it-IT', { 
                  day: '2-digit', 
                  month: '2-digit', 
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}</p>
                <h2>Dati di contatto</h2>
                <div class="contact-value">
                  <span class="contact-label">Nome:</span>
                  <span>${contactData.name || 'Non specificato'}</span>
                </div>
                <div class="contact-value">
                  <span class="contact-label">Email:</span>
                  <span>${contactData.email || 'Non specificato'}</span>
                </div>
                <div class="contact-value">
                  <span class="contact-label">Telefono:</span>
                  <span>${contactData.phone || 'Non specificato'}</span>
                </div>
              </div>`;

      // Aggiungi dettagli immobile se presenti
      if (propertyDetails?.title) {
        htmlContent += `
          <h2>Dettagli Immobile</h2>
          <div class="property-box">
            <h3>${propertyDetails.title}</h3>
            <p><strong>Riferimento:</strong> ${propertyDetails.ref || 'N/D'}</p>
            ${propertyDetails.url ? 
              `<p><a href="${propertyDetails.url}" style="color: #A3924D; font-weight: bold;">
                Visualizza immobile sul sito
               </a></p>` : ''}
          </div>`;
      }

      // Aggiungi il messaggio
      htmlContent += `
              <h2>Messaggio</h2>
              <div class="message-box">
                <p style="white-space: pre-wrap;">${contactData.message || 'Nessun messaggio'}</p>
              </div>
              
              <div class="footer">
                <p>Questa email è stata inviata automaticamente dal sito Molinaro Immobiliare.</p>
                <p>© ${new Date().getFullYear()} Molinaro Immobiliare - Via Pisana 327, 50018 Scandicci (FI)</p>
              </div>
            </div>
          </div>
        </body>
        </html>
      `;

      // Configurazione email
      const emailData = {
        sender: {
          name: "Molinaro Immobiliare",
          email: "noreply@molinaroimmobiliare.it",
        },
        to: [
          {
            email: notificationEmail || settings?.email || 'info@molinaroimmobiliare.it',
            name: "Staff Molinaro Immobiliare",
          },
        ],
        subject: propertyDetails?.title 
          ? `Nuova richiesta per: ${propertyDetails.title}` 
          : `Nuova richiesta di contatto da ${contactData.name}`,
        htmlContent,
      };

      // Invia email tramite Brevo (SendinBlue)
      const response = await fetch("https://api.brevo.com/v3/smtp/email", {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "api-key": BREVO_API_KEY,
        },
        body: JSON.stringify(emailData),
      });

      const responseData = await response.json();

      if (!response.ok) {
        console.error("Errore nell'invio email:", responseData);
        throw new Error(`Errore nell'invio email: ${JSON.stringify(responseData)}`);
      }

      return { success: true };
    } catch (error: any) {
      console.error('Errore nell\'invio della notifica email:', error);
      return { success: false, error: error.message };
    }
  };

  const submitContactForm = async (data: ContactFormData, propertyDetails?: {
    title?: string;
    ref?: string;
    url?: string;
  }) => {
    setLoading(true);
    try {
      // Salva il contatto nel database
      const { error } = await supabase
        .from('contacts')
        .insert([data]);

      if (error) throw error;

      // Invia la notifica email
      const emailResult = await sendEmailNotification(data, propertyDetails);
      if (!emailResult.success) {
        console.warn("L'email non è stata inviata, ma il contatto è stato registrato:", emailResult.error);
      }

      // Track form submission conversion
      trackConversion('form_submission');

      return { success: true, error: null };
    } catch (error: any) {
      console.error('Error submitting contact form:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    submitContactForm,
    handlePhoneClick,
    handleWhatsAppClick
  };
};